import FilterListIcon from '@mui/icons-material/FilterList';
import { CashSessionStatus } from '@/@types/cash-management';
import { Modal } from '@/components/common';
import { ModalTitle } from '@/components/common/Modal/common';
import { createDateFromString } from '@/utils/date';
import { DateRangePreset, RANGE_PRESETS } from '@/utils/date-range';
import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format, isAfter, isBefore } from 'date-fns';
import { MouseEvent, useState } from 'react';
import { cashSessionStatusLabel } from './CashSessionStatusLabel';

export type CashSessionStatusFilterOption = CashSessionStatus | 'todos';

const CASH_SESSION_STATUSES: CashSessionStatus[] = [
  'OPEN',
  'RECONCILIATION',
  'CLOSED',
];

interface CashSessionsFiltersModalProps {
  isOpen: boolean;
  startDate: string;
  endDate: string;
  status: CashSessionStatusFilterOption;
  // eslint-disable-next-line no-unused-vars
  onStartDateChange: (value: string) => void;
  // eslint-disable-next-line no-unused-vars
  onEndDateChange: (value: string) => void;
  // eslint-disable-next-line no-unused-vars
  onStatusChange: (value: CashSessionStatusFilterOption) => void;
  onClose: () => void;
  onApply: () => void;
}

export function CashSessionsFiltersModal({
  isOpen,
  startDate,
  endDate,
  status,
  onStartDateChange,
  onEndDateChange,
  onStatusChange,
  onClose,
  onApply,
}: CashSessionsFiltersModalProps) {
  const [rangeMenuAnchorEl, setRangeMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  const parsedStartDate = startDate
    ? createDateFromString(startDate)
    : new Date();
  const parsedEndDate = endDate ? createDateFromString(endDate) : new Date();
  const isRangeMenuOpen = Boolean(rangeMenuAnchorEl);
  const isRangeInvalid = isAfter(parsedStartDate, parsedEndDate);

  const handleStartDateChange = (nextDate: Date | null) => {
    if (!nextDate) return;

    onStartDateChange(format(nextDate, 'yyyy-MM-dd'));

    if (isAfter(nextDate, parsedEndDate)) {
      onEndDateChange(format(nextDate, 'yyyy-MM-dd'));
    }
  };

  const handleEndDateChange = (nextDate: Date | null) => {
    if (!nextDate) return;

    onEndDateChange(format(nextDate, 'yyyy-MM-dd'));

    if (isBefore(nextDate, parsedStartDate)) {
      onStartDateChange(format(nextDate, 'yyyy-MM-dd'));
    }
  };

  const handleOpenRangeMenu = (event: MouseEvent<HTMLElement>) => {
    setRangeMenuAnchorEl(event.currentTarget);
  };

  const handleCloseRangeMenu = () => {
    setRangeMenuAnchorEl(null);
  };

  const handleApplyPreset = (preset: DateRangePreset) => {
    const { startDate: presetStartDate, endDate: presetEndDate } =
      preset.getRange();

    onStartDateChange(format(presetStartDate, 'yyyy-MM-dd'));
    onEndDateChange(format(presetEndDate, 'yyyy-MM-dd'));
    handleCloseRangeMenu();
  };

  return (
    <Modal open={isOpen} onClose={onClose} fullWidth maxWidth="xs">
      <ModalTitle title="Filtros" onClose={onClose} />
      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={2} pt={1}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <DatePicker
              label="Data inicial"
              value={parsedStartDate}
              onChange={handleStartDateChange}
              sx={{ flex: 1 }}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flex: 1 }}
            >
              <DatePicker
                label="Data final"
                value={parsedEndDate}
                onChange={handleEndDateChange}
                sx={{ flex: 1 }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: isRangeInvalid,
                    helperText: isRangeInvalid
                      ? 'Data final não pode ser menor que a data inicial.'
                      : undefined,
                  },
                }}
              />
              <Tooltip title="Faixas rápidas" disableInteractive>
                <IconButton
                  aria-label="Faixas rápidas"
                  aria-controls={
                    isRangeMenuOpen ? 'cash-date-range-menu' : undefined
                  }
                  aria-haspopup="true"
                  aria-expanded={isRangeMenuOpen ? 'true' : undefined}
                  size="small"
                  onClick={handleOpenRangeMenu}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
              <Menu
                id="cash-date-range-menu"
                anchorEl={rangeMenuAnchorEl}
                open={isRangeMenuOpen}
                onClose={handleCloseRangeMenu}
              >
                {RANGE_PRESETS.map((preset) => (
                  <MenuItem
                    key={preset.id}
                    onClick={() => handleApplyPreset(preset)}
                  >
                    {preset.label}
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          </Stack>
          <FormControl fullWidth>
            <InputLabel id="cash-session-status-label">Status</InputLabel>
            <Select
              labelId="cash-session-status-label"
              value={status}
              label="Status"
              onChange={({ target }) =>
                onStatusChange(target.value as CashSessionStatusFilterOption)
              }
            >
              <MenuItem value="todos">Todos</MenuItem>
              {CASH_SESSION_STATUSES.map((item) => (
                <MenuItem key={item} value={item}>
                  {cashSessionStatusLabel[item]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        <Button onClick={onClose}>Fechar</Button>
        <Button variant="contained" onClick={onApply} disabled={isRangeInvalid}>
          Aplicar
        </Button>
      </DialogActions>
    </Modal>
  );
}
